import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle2, Award, Gift } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { tierLabel } from "../../lib/tier";

// Subtle tier-themed gradient for the customer hero. Falls back to flat
// marble for ospite. Designed to read on top of the cream-marble bg.
const TIER_GRADIENT = {
  ospite: "bg-napoli-marble",
  amico:
    "bg-[linear-gradient(135deg,_rgba(108,21,29,0.06)_0%,_rgba(248,243,232,1)_55%,_rgba(197,160,77,0.10)_100%)]",
  famiglia:
    "bg-[linear-gradient(135deg,_rgba(108,21,29,0.14)_0%,_rgba(248,243,232,1)_50%,_rgba(108,21,29,0.10)_100%)]",
  padrone:
    "bg-[linear-gradient(135deg,_rgba(197,160,77,0.20)_0%,_rgba(248,243,232,1)_50%,_rgba(197,160,77,0.32)_100%)]",
};

const QUICK_ADDS = [10, 25, 50, 100];

const fmtAmount = (raw) => {
  // Strip non-digit/non-dot, allow at most one dot, max 2 decimals
  let v = String(raw || "").replace(/[^0-9.]/g, "");
  const parts = v.split(".");
  if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
  const [intPart, decPart] = v.split(".");
  if (decPart !== undefined) {
    return `${intPart || "0"}.${decPart.slice(0, 2)}`;
  }
  return intPart;
};

const dollarsToCents = (str) => {
  if (!str) return 0;
  const f = parseFloat(str);
  if (!Number.isFinite(f)) return 0;
  return Math.round(f * 100);
};

const initialsOf = (c) => {
  const f = (c?.first_name || "").trim();
  const l = (c?.last_name || "").trim();
  return ((f.charAt(0) + l.charAt(0)) || "?").toUpperCase();
};

const previewPoints = (cents, mult, ppd = 1) => {
  if (!cents || cents <= 0) return 0;
  const base = Math.floor((cents / 100) * ppd);
  return Math.floor(base * (mult || 1));
};

const TabButton = ({ active, onClick, label, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={[
      "flex-1 py-5 font-body font-semibold text-[12px] tracking-[0.22em] uppercase transition-colors",
      active
        ? "bg-napoli-oxblood text-napoli-cream"
        : "bg-napoli-cream-warm text-napoli-oxblood border border-napoli-oxblood/60 hover:bg-napoli-cream",
    ].join(" ")}
  >
    {label}
  </button>
);

const SuccessSplash = ({ awarded, customerName, newBalance, tierChanged, newTier, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 3200);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div
      data-testid="award-success"
      className="bg-napoli-marble border border-napoli-gold/40 px-8 py-12 text-center"
    >
      <CheckCircle2 size={96} className="mx-auto text-napoli-gold" strokeWidth={1.4} />
      <p className="mt-6 font-display font-bold text-[24px] sm:text-[28px] text-napoli-espresso">
        +{awarded} points awarded to {customerName}
      </p>
      <p className="mt-2 font-body italic text-[14px] text-napoli-espresso-soft">
        New balance: <span className="font-semibold not-italic text-napoli-espresso">{newBalance.toLocaleString()} pts</span>
      </p>
      {tierChanged && newTier ? (
        <p className="mt-6 font-flourish text-[34px] text-napoli-gold">
          Promoted to {tierLabel(newTier)}!
        </p>
      ) : null}
      <button
        type="button"
        onClick={onDone}
        data-testid="award-success-done"
        className="mt-10 inline-flex items-center justify-center bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase px-10 py-4 hover:bg-napoli-oxblood-dark transition-colors"
      >
        Done
      </button>
    </div>
  );
};

const RedeemSuccessSplash = ({ rewardName, customerName, deducted, newBalance, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 3200);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div
      data-testid="redeem-success"
      className="bg-napoli-marble border border-napoli-gold/40 px-8 py-12 text-center"
    >
      <CheckCircle2 size={96} className="mx-auto text-napoli-gold" strokeWidth={1.4} />
      <p className="mt-6 font-display font-bold text-[22px] sm:text-[26px] text-napoli-espresso">
        {rewardName} redeemed for {customerName}.
      </p>
      <p className="mt-2 font-body italic text-[14px] text-napoli-espresso-soft">
        −{deducted.toLocaleString()} pts · new balance{" "}
        <span className="font-semibold not-italic text-napoli-espresso">{newBalance.toLocaleString()} pts</span>
      </p>
      <button
        type="button"
        onClick={onDone}
        data-testid="redeem-success-done"
        className="mt-10 inline-flex items-center justify-center bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase px-10 py-4 hover:bg-napoli-oxblood-dark transition-colors"
      >
        Done
      </button>
    </div>
  );
};

const CustomerActionPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [tab, setTab] = useState("award"); // 'award' | 'redeem'

  // Award state
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [awardSuccess, setAwardSuccess] = useState(null);

  // Redeem state
  const [confirmingRewardId, setConfirmingRewardId] = useState(null);
  const [redeemSuccess, setRedeemSuccess] = useState(null);

  const reload = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.post("/staff/lookup-customer", {
        qr_payload: `napoli:cust:${customerId}`,
      });
      setCustomer(data.customer);
    } catch (e) {
      const code = e?.response?.data?.detail?.error;
      setErr(code === "customer_not_found" ? "We couldn't find that customer." : "Something went wrong loading this customer.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  if (loading) {
    return (
      <div data-testid="customer-action-loading" className="flex items-center justify-center min-h-[60vh]">
        <Loader2 size={28} className="animate-spin text-napoli-gold" />
      </div>
    );
  }

  if (err || !customer) {
    return (
      <div className="px-6 pt-12 text-center">
        <p data-testid="customer-action-error" className="font-display font-bold text-[20px] text-napoli-cream">
          {err || "Customer unavailable."}
        </p>
        <Link
          to="/staff/scanner"
          data-testid="customer-action-back-to-scanner"
          className="mt-6 inline-block font-body text-[12px] tracking-[0.22em] uppercase text-napoli-gold border-b border-napoli-gold pb-1"
        >
          ← Back to scanner
        </Link>
      </div>
    );
  }

  const c = customer;
  const fullName = [c.first_name, c.last_name].filter(Boolean).join(" ") || "Member";
  const cents = dollarsToCents(amount);
  const points = previewPoints(cents, c.tier_multiplier);
  const memberSince = c.member_since ? new Date(c.member_since).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "—";

  const submitAward = async () => {
    if (cents <= 0 || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/staff/award-points", {
        customer_id: c.id,
        amount_cents: cents,
        notes: notes.trim() || undefined,
      });
      setAwardSuccess({
        awarded: data.points_awarded,
        newBalance: data.new_balance,
        tierChanged: !!data.tier_changed,
        newTier: data.new_tier,
      });
      // Update local copy so subsequent redeems see fresh balance
      setCustomer((prev) => prev ? { ...prev, points_balance: data.new_balance, current_tier: data.new_tier || prev.current_tier } : prev);
      setAmount("");
      setNotes("");
      setShowNotes(false);
    } catch (e) {
      const code = e?.response?.data?.detail?.error;
      toast.error(
        code === "invalid_amount" ? "Check amount looks invalid." :
        code === "customer_not_found" ? "Customer disappeared. Try scanning again." :
        "We couldn't award points. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitRedeem = async (rewardId) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/staff/redeem-reward", {
        customer_id: c.id,
        reward_id: rewardId,
      });
      setRedeemSuccess({
        rewardName: data.reward_name,
        newBalance: data.new_balance,
        deducted: data.points_deducted,
      });
      setCustomer((prev) => prev ? { ...prev, points_balance: data.new_balance } : prev);
      setConfirmingRewardId(null);
    } catch (e) {
      const code = e?.response?.data?.detail?.error;
      toast.error(
        code === "insufficient_points" ? "Not enough points for that reward." :
        code === "tier_locked" ? "Their tier doesn't unlock that yet." :
        code === "reward_inactive" ? "That reward is no longer available." :
        "We couldn't redeem that. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const allRewards = c.all_rewards || [];
  const eligible = allRewards.filter((r) => r.eligible);
  const ineligible = allRewards.filter((r) => !r.eligible);

  return (
    <section data-testid="customer-action-page" className="pb-12">
      {/* Customer summary card */}
      <div
        data-testid="customer-summary-card"
        className={`text-napoli-espresso px-6 py-7 border-b border-napoli-gold/30 ${TIER_GRADIENT[c.current_tier] || TIER_GRADIENT.ospite}`}
      >
        <div className="max-w-xl mx-auto flex items-start gap-5">
          <div
            aria-hidden
            className="h-[60px] w-[60px] rounded-full bg-napoli-cream-warm border-2 border-napoli-gold text-napoli-oxblood flex items-center justify-center font-display font-bold text-[20px] flex-shrink-0"
          >
            {initialsOf(c)}
          </div>
          <div className="flex-1 min-w-0">
            <h1 data-testid="customer-name" className="font-display font-bold text-[24px] sm:text-[26px] leading-tight">
              {fullName}
            </h1>
            <p className="mt-1 font-body italic text-[12px] text-napoli-espresso-soft">
              Member since {memberSince} · code {c.customer_code}
            </p>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <span
                data-testid="customer-tier-badge"
                className="inline-flex items-center gap-2 px-3 py-1 bg-napoli-oxblood text-napoli-cream font-body text-[10px] tracking-[0.22em] uppercase"
              >
                {tierLabel(c.current_tier)} · {(c.tier_multiplier ?? 1).toFixed(2)}× multiplier
              </span>
              <span
                data-testid="customer-points-balance"
                className="inline-flex items-center px-3 py-1 border border-napoli-oxblood/40 text-napoli-oxblood font-body font-semibold text-[11px] tracking-[0.18em] uppercase"
              >
                {Number(c.points_balance || 0).toLocaleString()} pts
              </span>
            </div>
            <p
              data-testid="customer-recent-visits"
              className="mt-3 font-body font-medium italic text-[12px] text-napoli-gold"
              style={{ textShadow: "0 1px 2px rgba(31,22,17,0.18)" }}
            >
              {c.recent_visits || 0} visit{c.recent_visits === 1 ? "" : "s"} in the last 30 days
            </p>
          </div>
        </div>
      </div>

      {/* Action area */}
      <div className="max-w-xl mx-auto px-6 mt-6">
        {awardSuccess ? (
          <SuccessSplash
            awarded={awardSuccess.awarded}
            customerName={fullName}
            newBalance={awardSuccess.newBalance}
            tierChanged={awardSuccess.tierChanged}
            newTier={awardSuccess.newTier}
            onDone={() => {
              setAwardSuccess(null);
              navigate("/staff/scanner");
            }}
          />
        ) : redeemSuccess ? (
          <RedeemSuccessSplash
            rewardName={redeemSuccess.rewardName}
            customerName={fullName}
            deducted={redeemSuccess.deducted}
            newBalance={redeemSuccess.newBalance}
            onDone={() => {
              setRedeemSuccess(null);
              navigate("/staff/scanner");
            }}
          />
        ) : (
          <>
            <div className="flex gap-3" role="tablist">
              <TabButton
                active={tab === "award"}
                onClick={() => setTab("award")}
                label="Award points"
                testId="tab-award"
              />
              <TabButton
                active={tab === "redeem"}
                onClick={() => setTab("redeem")}
                label="Redeem reward"
                testId="tab-redeem"
              />
            </div>

            {tab === "award" ? (
              <div data-testid="panel-award" className="mt-8">
                <label className="block text-center">
                  <span className="block font-body font-semibold text-[11px] tracking-[0.22em] uppercase text-napoli-cream/70">
                    Check amount
                  </span>
                  <div className="mt-3 flex items-baseline justify-center gap-2">
                    <span className="font-display font-bold text-[32px] text-napoli-gold">$</span>
                    <input
                      data-testid="award-amount-input"
                      type="text"
                      inputMode="decimal"
                      value={amount}
                      onChange={(e) => setAmount(fmtAmount(e.target.value))}
                      placeholder="0.00"
                      className="w-[8ch] bg-transparent border-0 border-b-2 border-napoli-gold/60 focus:border-napoli-gold text-napoli-cream font-mono font-semibold text-[44px] sm:text-[56px] text-center focus:outline-none placeholder:text-napoli-cream/30"
                    />
                  </div>
                </label>

                {/* Quick-add chips — fast tap targets for common check sizes */}
                <div
                  data-testid="award-quick-add-chips"
                  className="mt-5 flex flex-wrap justify-center gap-2"
                  role="group"
                  aria-label="Quick add to check amount"
                >
                  {QUICK_ADDS.map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`award-quick-add-${n}`}
                      onClick={() => {
                        const current = parseFloat(amount || "0") || 0;
                        const next = (current + n).toFixed(2).replace(/\.00$/, "");
                        setAmount(fmtAmount(next));
                      }}
                      className="inline-flex items-center justify-center min-w-[64px] px-3 py-2 bg-napoli-espresso-soft border border-napoli-gold/40 hover:border-napoli-gold text-napoli-cream font-body font-semibold text-[14px] tracking-[0.05em] hover:bg-napoli-espresso/60 transition-colors"
                    >
                      +${n}
                    </button>
                  ))}
                  {amount ? (
                    <button
                      type="button"
                      data-testid="award-quick-add-clear"
                      onClick={() => setAmount("")}
                      className="inline-flex items-center justify-center px-3 py-2 border border-napoli-rose/40 hover:border-napoli-rose text-napoli-rose font-body font-semibold text-[11px] tracking-[0.18em] uppercase hover:bg-napoli-rose/10 transition-colors"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>

                <div className="mt-6 text-center" data-testid="award-preview">
                  {cents > 0 ? (
                    <>
                      <p className="font-display font-semibold text-[20px] text-napoli-gold">
                        {c.first_name || "Customer"} earns +{points.toLocaleString()} pts
                      </p>
                      <p className="mt-2 font-body italic text-[12px] text-napoli-cream/60">
                        ${(cents / 100).toFixed(2)} × {(c.tier_multiplier ?? 1).toFixed(2)} = {points} pts
                      </p>
                    </>
                  ) : (
                    <p className="font-body italic text-[13px] text-napoli-cream/50">
                      Enter check amount above.
                    </p>
                  )}
                </div>

                <div className="mt-6">
                  {!showNotes ? (
                    <button
                      type="button"
                      data-testid="award-add-note"
                      onClick={() => setShowNotes(true)}
                      className="font-body font-medium text-[12px] tracking-[0.05em] text-napoli-gold hover:text-napoli-gold-light underline underline-offset-4"
                    >
                      + Add note
                    </button>
                  ) : (
                    <textarea
                      data-testid="award-notes-input"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value.slice(0, 200))}
                      placeholder="e.g. birthday celebration, anniversary"
                      rows={2}
                      className="w-full bg-napoli-espresso-soft border border-napoli-gold/30 text-napoli-cream px-4 py-3 font-body text-[13px] focus:outline-none focus:border-napoli-gold transition-colors"
                    />
                  )}
                </div>

                <button
                  type="button"
                  data-testid="award-submit"
                  onClick={submitAward}
                  disabled={cents <= 0 || submitting}
                  className="mt-8 w-full bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-5 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Awarding…
                    </>
                  ) : (
                    "Award points"
                  )}
                </button>
              </div>
            ) : (
              <div data-testid="panel-redeem" className="mt-8 space-y-6">
                {eligible.length === 0 ? (
                  <p
                    data-testid="redeem-empty"
                    className="bg-napoli-espresso-soft border border-napoli-gold/20 px-5 py-6 text-center font-body italic text-[13px] text-napoli-cream/70"
                  >
                    No rewards available for {c.first_name || "them"} right now. Earn more points first.
                  </p>
                ) : (
                  <ul data-testid="redeem-eligible-list" className="space-y-3">
                    {eligible.map((r) => (
                      <li key={r.id}>
                        <button
                          type="button"
                          data-testid={`reward-${r.id}`}
                          onClick={() => setConfirmingRewardId(r.id)}
                          className="w-full flex items-center gap-4 bg-napoli-espresso-soft hover:bg-napoli-espresso/60 border border-napoli-gold/20 hover:border-napoli-gold/60 transition-colors px-4 py-3 text-left"
                        >
                          {r.image_url ? (
                            <img
                              src={r.image_url}
                              alt=""
                              className="h-16 w-16 object-cover flex-shrink-0"
                              loading="lazy"
                            />
                          ) : (
                            <div className="h-16 w-16 bg-napoli-cream-warm flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-display font-semibold text-[15px] text-napoli-cream truncate">{r.name}</p>
                            <p className="mt-0.5 font-body italic text-[11px] text-napoli-cream/60 truncate">
                              {r.description}
                            </p>
                          </div>
                          <span className="font-body font-semibold text-[12px] tracking-[0.18em] uppercase text-napoli-gold whitespace-nowrap">
                            {r.points_cost.toLocaleString()} pts
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {ineligible.length > 0 ? (
                  <div data-testid="redeem-ineligible-list">
                    <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-cream/40">
                      Not yet
                    </p>
                    <ul className="mt-3 space-y-2 opacity-60">
                      {ineligible.map((r) => (
                        <li
                          key={r.id}
                          data-testid={`reward-locked-${r.id}`}
                          className="flex items-center gap-4 bg-napoli-espresso-soft border border-napoli-gold/10 px-4 py-2.5"
                        >
                          {r.image_url ? (
                            <img src={r.image_url} alt="" className="h-12 w-12 object-cover flex-shrink-0 grayscale" />
                          ) : (
                            <div className="h-12 w-12 bg-napoli-cream-warm flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-body font-semibold text-[13px] text-napoli-cream/80 truncate">{r.name}</p>
                            <p className="mt-0.5 font-body italic text-[11px] text-napoli-cream/50 truncate">
                              {r.reason_ineligible === "tier_locked"
                                ? `Needs ${tierLabel(r.tier_required)} tier`
                                : `Needs ${r.points_short.toLocaleString()} more pts`}
                            </p>
                          </div>
                          <span className="font-body text-[11px] tracking-[0.18em] uppercase text-napoli-cream/50 whitespace-nowrap">
                            {r.points_cost.toLocaleString()} pts
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            )}
          </>
        )}

        {!awardSuccess && !redeemSuccess ? (
          <>
            {/* Inline recent activity — gives staff context for what this
                customer has been up to without leaving the page */}
            {Array.isArray(c.recent_transactions) && c.recent_transactions.length > 0 ? (
              <div data-testid="customer-recent-tx" className="mt-10">
                <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-cream/55">
                  Recent activity
                </p>
                <ul className="mt-3 bg-napoli-espresso-soft/50 border border-napoli-gold/15 divide-y divide-napoli-gold/10">
                  {c.recent_transactions.map((t) => {
                    const isEarn = t.type === "earn";
                    return (
                      <li
                        key={t.id}
                        data-testid={`customer-recent-tx-${t.id}`}
                        className="flex items-center gap-3 px-4 py-3"
                      >
                        <span
                          aria-hidden
                          className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                            isEarn
                              ? "bg-napoli-gold/15 text-napoli-gold"
                              : "bg-napoli-oxblood/20 text-napoli-rose"
                          }`}
                        >
                          {isEarn ? <Award size={13} /> : <Gift size={13} />}
                        </span>
                        <span className="flex-1 min-w-0 font-body text-[12px] sm:text-[13px] text-napoli-cream truncate">
                          {isEarn ? (
                            <>
                              Earned{" "}
                              <span className={isEarn ? "text-napoli-gold font-semibold" : ""}>
                                {t.points_delta > 0 ? "+" : ""}
                                {t.points_delta} pts
                              </span>
                              {t.amount_spent != null ? (
                                <span className="text-napoli-cream/60"> · ${t.amount_spent.toFixed(2)}</span>
                              ) : null}
                              {t.notes ? (
                                <span className="text-napoli-cream/55"> · {t.notes}</span>
                              ) : null}
                            </>
                          ) : (
                            <>
                              Redeemed{" "}
                              <span className="font-semibold">{t.reward_name || "a reward"}</span>{" "}
                              <span className="text-napoli-rose">{t.points_delta} pts</span>
                            </>
                          )}
                        </span>
                        <span className="font-body text-[10px] tracking-[0.12em] uppercase text-napoli-cream/45 whitespace-nowrap">
                          {t.created_at_relative}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            <div className="mt-12 text-center">
              <Link
                to="/staff/scanner"
                data-testid="back-to-scanner"
                className="inline-flex items-center gap-2 font-body text-[12px] tracking-[0.22em] uppercase text-napoli-cream/70 hover:text-napoli-gold transition-colors"
              >
                <ArrowLeft size={14} />
                Back to scanner
              </Link>
            </div>
          </>
        ) : null}
      </div>

      {/* Confirm modal */}
      {confirmingRewardId ? (
        <div
          data-testid="redeem-confirm-modal"
          className="fixed inset-0 z-40 flex items-center justify-center bg-napoli-espresso/80 px-6"
          onClick={() => setConfirmingRewardId(null)}
        >
          {(() => {
            const r = allRewards.find((x) => x.id === confirmingRewardId);
            if (!r) return null;
            return (
              <div
                onClick={(e) => e.stopPropagation()}
                className="bg-napoli-marble text-napoli-espresso max-w-sm w-full px-6 py-7 border border-napoli-gold/40"
              >
                <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">
                  Confirm
                </p>
                <p className="mt-3 font-display font-bold text-[20px]">
                  Redeem {r.name} for {r.points_cost.toLocaleString()} pts?
                </p>
                <p className="mt-2 font-body italic text-[13px] text-napoli-espresso-soft">
                  {fullName}&rsquo;s balance will become{" "}
                  {(c.points_balance - r.points_cost).toLocaleString()} pts.
                </p>
                <div className="mt-6 flex gap-3">
                  <button
                    type="button"
                    onClick={() => setConfirmingRewardId(null)}
                    data-testid="redeem-cancel"
                    className="flex-1 border border-napoli-espresso/40 text-napoli-espresso font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-cream-warm transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={() => submitRedeem(r.id)}
                    data-testid="redeem-confirm"
                    className="flex-1 bg-napoli-oxblood text-napoli-cream font-body font-semibold text-[12px] tracking-[0.22em] uppercase py-3 hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-60"
                  >
                    {submitting ? "…" : "Confirm"}
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      ) : null}
    </section>
  );
};

export default CustomerActionPage;
